import type { VendorConfig, ConfigApiResponse, InitConfig } from './types';
import {
  AuthenticationError,
  NotFoundError,
  NetworkError,
  ValidationError,
  TimeoutError,
  SDKError
} from './types';

/**
 * Configuration Manager
 *
 * Handles fetching, caching, and ETag-based conditional requests for remote SDK configurations.
 * No polling - uses HTTP 304 Not Modified for efficient config checking.
 * Includes retry logic with exponential backoff for transient failures.
 */

/**
 * Validate config structure to ensure it has required fields
 */
function validateConfig(config: any): config is VendorConfig {
  if (!config || typeof config !== 'object') {
    return false;
  }

  // Check features object exists
  if (!config.features || typeof config.features !== 'object') {
    return false;
  }

  // Check tracking config
  if (!config.features.tracking || typeof config.features.tracking !== 'object') {
    return false;
  }
  if (typeof config.features.tracking.enabled !== 'boolean') {
    return false;
  }

  // b2cMode is optional, but if present must be boolean
  if (config.features.b2cMode !== undefined && 
      typeof config.features.b2cMode !== 'boolean') {
    return false;
  }

  // Check version and updatedAt
  if (typeof config.version !== 'string' || typeof config.updatedAt !== 'string') {
    return false;
  }

  return true;
}

/**
 * Fetch with timeout support using AbortController
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number = 10000
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new TimeoutError(`Request timeout after ${timeoutMs}ms`, timeoutMs);
    }
    throw error;
  }
}

export class ConfigManager {
  private vendorId: string;
  private apiKey: string;
  private apiUrl: string;
  private config: VendorConfig | null = null;
  private etag: string | null = null;

  constructor({ vendorId, apiKey, apiUrl }: InitConfig) {
    this.vendorId = vendorId;
    this.apiKey = apiKey;
    this.apiUrl = apiUrl || 'https://firstdistro.com';
  }

  /**
   * Fetch configuration from API with ETag support
   * Uses cached config first for instant boot, then checks for updates using ETag
   *
   * ETag flow:
   * 1. Send If-None-Match header with cached ETag
   * 2. If server returns 304: config unchanged, use cache
   * 3. If server returns 200: config changed, update cache
   *
   * Includes retry logic with exponential backoff for transient failures (up to 3 attempts)
   */
  async fetchConfig(): Promise<VendorConfig> {
    // Load cached config and ETag from localStorage
    const cached = this.loadCachedData();
    if (cached) {
      this.config = cached.config;
      this.etag = cached.etag;
    }

    // Retry with exponential backoff for transient failures
    const maxRetries = 3;
    let lastError: SDKError | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Build request headers
        const headers: HeadersInit = {
          'X-API-Key': this.apiKey,
          'Content-Type': 'application/json'
        };

        // Add ETag for conditional request if available
        if (this.etag) {
          headers['If-None-Match'] = this.etag;
        }

        // Fetch config from API with timeout
        const response = await fetchWithTimeout(
          `${this.apiUrl}/api/vendors/${this.vendorId}/sdk-config`,
          { headers },
          10000 // 10 second timeout
        );

        // Handle 304 Not Modified (config unchanged)
        if (response.status === 304) {
          if (this.config) {
            // console.log('[FirstDistro] Config unchanged (304), using cached');
            return this.config;
          }
          // 304 but no cache? Fetch without ETag
          return this.fetchConfigFresh();
        }

        // Handle HTTP errors
        if (!response.ok) {
          if (response.status === 401) {
            throw new AuthenticationError();
          }
          if (response.status === 404) {
            throw new NotFoundError();
          }
          // 5xx errors are retryable, 4xx are not
          const retryable = response.status >= 500;
          throw new SDKError(
            `Failed to fetch config: ${response.status}`,
            'HTTP_ERROR',
            response.status,
            retryable
          );
        }

        // Parse response
        const data: ConfigApiResponse = await response.json();
        if (!data.success || !data.config) {
          throw new ValidationError('Invalid config response: missing success or config field');
        }

        // Validate config structure
        if (!validateConfig(data.config)) {
          throw new ValidationError('Invalid config structure: missing required fields');
        }

        // Extract and store ETag from response
        const newETag = response.headers.get('ETag');
        if (newETag) {
          this.etag = newETag;
        }

        // Update config and cache
        this.config = data.config;
        this.saveCachedData(this.config, this.etag);

        // console.log('[FirstDistro] Config fetched successfully');
        return this.config;

      } catch (error) {
        // Convert unknown errors to SDKError
        if (!(error instanceof SDKError)) {
          if (error instanceof Error) {
            lastError = new NetworkError(error.message, error);
          } else {
            lastError = new NetworkError('Unknown network error');
          }
        } else {
          lastError = error;
        }

        // Don't retry non-retryable errors
        if (!lastError.retryable) {
          // Fallback to cached config if available
          if (this.config) {
            console.warn('[FirstDistro] Non-retryable error, using cached config:', lastError);
            return this.config;
          }
          // No cache: throw error
          console.error('[FirstDistro] Failed to fetch config and no cache available:', lastError);
          throw lastError;
        }

        // For retryable errors, retry with exponential backoff
        if (attempt < maxRetries - 1) {
          const delayMs = Math.min(1000 * Math.pow(2, attempt), 5000); // Cap at 5s
          console.warn(
            `[FirstDistro] Retryable error on attempt ${attempt + 1}/${maxRetries}, ` +
            `retrying in ${delayMs}ms:`,
            lastError
          );
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
    }

    // All retries exhausted
    if (this.config) {
      console.warn('[FirstDistro] All retries exhausted, using cached config:', lastError);
      return this.config;
    }

    // No cache available: throw last error
    console.error('[FirstDistro] Failed to fetch config after all retries and no cache available:', lastError);
    throw lastError;
  }

  /**
   * Fetch config without ETag (fresh fetch)
   * Used as fallback when 304 received but no cache available
   */
  private async fetchConfigFresh(): Promise<VendorConfig> {
    const response = await fetchWithTimeout(
      `${this.apiUrl}/api/vendors/${this.vendorId}/sdk-config`,
      {
        headers: {
          'X-API-Key': this.apiKey,
          'Content-Type': 'application/json'
        }
      },
      10000 // 10 second timeout
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new AuthenticationError();
      }
      if (response.status === 404) {
        throw new NotFoundError();
      }
      throw new SDKError(
        `Failed to fetch config: ${response.status}`,
        'HTTP_ERROR',
        response.status,
        response.status >= 500
      );
    }

    const data: ConfigApiResponse = await response.json();
    if (!data.success || !data.config) {
      throw new ValidationError('Invalid config response: missing success or config field');
    }

    // Validate config structure
    if (!validateConfig(data.config)) {
      throw new ValidationError('Invalid config structure: missing required fields');
    }

    const newETag = response.headers.get('ETag');
    if (newETag) {
      this.etag = newETag;
    }

    this.config = data.config;
    this.saveCachedData(this.config, this.etag);

    return this.config;
  }

  /**
   * Load cached config and ETag from localStorage
   * Returns null if cache expired or unavailable
   */
  private loadCachedData(): { config: VendorConfig; etag: string | null } | null {
    try {
      const cached = localStorage.getItem('firstdistro_config');
      if (!cached) return null;

      const { config, etag, cachedAt } = JSON.parse(cached);

      // Expire cache after 24 hours
      const age = Date.now() - new Date(cachedAt).getTime();
      if (age > 24 * 60 * 60 * 1000) return null;

      return { config, etag: etag || null };
    } catch {
      return null;
    }
  }

  /**
   * Save config and ETag to localStorage
   * Handles errors gracefully (private browsing, quota exceeded)
   */
  private saveCachedData(config: VendorConfig, etag: string | null): void {
    try {
      localStorage.setItem('firstdistro_config', JSON.stringify({
        config,
        etag,
        cachedAt: new Date().toISOString()
      }));
    } catch (error) {
      // Ignore localStorage errors (private browsing, quota exceeded)
      console.warn('[FirstDistro] Failed to cache config:', error);
    }
  }

  /**
   * Get cached configuration from localStorage (legacy method for backward compatibility)
   * Returns null if cache expired or unavailable
   * @deprecated Use loadCachedData() internally
   */
  getCachedConfig(): VendorConfig | null {
    const cached = this.loadCachedData();
    return cached ? cached.config : null;
  }

  /**
   * Get current configuration
   * Returns null if not fetched yet
   */
  getConfig(): VendorConfig | null {
    return this.config;
  }

  /**
   * Get vendor ID (public accessor)
   */
  getVendorId(): string {
    return this.vendorId;
  }

  /**
   * Get API key (public accessor)
   */
  getApiKey(): string {
    return this.apiKey;
  }

  /**
   * Get API URL (public accessor)
   */
  getApiUrl(): string {
    return this.apiUrl;
  }
}

